import { useTranslation } from 'react-i18next'
import { Col, Row, Button, Toast, ToastContainer } from 'react-bootstrap'
import { useState } from 'react'
import PropTypes from 'prop-types'
import FullLogo from '../../assets/logos/full-logo.png'
import ProfileAvatar from '../profileAvatar'
import theme from '../../constants/theme'
import PrimaryButton from '../buttons/PrimaryButton'
import {
  ExternalLink,
  FooterContent,
  JobApplicationPositionCard,
  Typography,
  FullPageContainer,
  ScrollableContainer,
  StickyFooter,
  FooterText,
  InfoCard,
} from '../styled'
import LanguagesDropdown from '../languageDropdwn'
import {
  formatCountryName,
  formatWebsiteUrl,
} from '../../utilities/stringUtilities'

function TalentPoolInfo({ talentPool, company, onApplyClick }) {
  const { t } = useTranslation()
  const [showToast, setShowToast] = useState(false)

  const handleExternalLinkClick = (url, _linkType) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <FullPageContainer>
      <ScrollableContainer className="mt-3 fw-light">
        <Row className="my-2 align-items-end">
          <Col xs="auto">
            <img height="auto" width="150px" src={FullLogo} alt="" />
          </Col>
          <Col />
          <Col xs="auto">
            <LanguagesDropdown />
          </Col>
        </Row>
        <Row>
          <JobApplicationPositionCard
            className="h-100 px-4 mb-5"
            style={{ boxShadow: '0px 15px 20px 0px rgba(18, 38, 63, 0.15)' }}
          >
            <Row className="ms-0 m-3 mb-3 align-items-center">
              <Col>
                <Typography
                  bold
                  size="19px"
                  color={theme.talentinoBlack}
                  style={{ lineHeight: '1.4' }}
                >
                  {t('pages.talentPoolInvitation', {
                    company: company?.companyName,
                    talentPool: talentPool?.name,
                  })}
                </Typography>
              </Col>
              <Col className="d-flex gap-2 justify-content-end" sm="auto">
                <PrimaryButton
                  backgroundcolor={theme.brandColorOrange}
                  hovercolor={theme.talentinoDarkerBlue}
                  onClick={onApplyClick}
                  width="150px"
                >
                  {t('pages.joinTalentPool')}
                </PrimaryButton>
              </Col>
            </Row>
            <Row id="talent-pool-info" className="mb-3 justify-content-center">
              <Col xs={12} lg={10}>
                <Row className="mb-3">
                  <InfoCard className="px-4 py-3">
                    <Typography
                      bold
                      size="20px"
                      color={theme.talentinoBlack}
                      style={{ lineHeight: '1.3' }}
                    >
                      {talentPool.name}
                    </Typography>
                    <Typography
                      className="mt-2"
                      size="14px"
                      regular
                      style={{ color: '#4B5563' }}
                    >
                      {talentPool.description}
                    </Typography>

                    {(talentPool.careerPageUrl || talentPool.resourceLinks) && (
                      <div className="mt-3">
                        <Typography
                          bold
                          size="14px"
                          className="mb-2"
                          color={theme.talentinoBlue}
                        >
                          {t('pages.externalResources')}
                        </Typography>
                        <div className="d-flex flex-wrap gap-2">
                          {talentPool.careerPageUrl && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleExternalLinkClick(
                                  talentPool.careerPageUrl,
                                  'career-page',
                                )
                              }
                            >
                              {t('pages.careerPage')}
                            </Button>
                          )}
                          {talentPool.resourceLinks?.map((link, index) => (
                            <Button
                              key={index}
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                handleExternalLinkClick(
                                  link.url,
                                  `resource-${index}`,
                                )
                              }
                            >
                              {link.title}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </InfoCard>
                </Row>
                <Row>
                  <InfoCard className="px-4 py-3">
                    <Row className="d-flex align-items-center">
                      <Col sm="auto" className="mb-auto">
                        {company?.logo ? (
                          <img
                            src={company.logo}
                            alt={`${company?.companyName} logo`}
                            className="img-fluid"
                            style={{ maxHeight: '60px', maxWidth: '100%' }}
                          />
                        ) : (
                          <ProfileAvatar name={company?.companyName} />
                        )}
                      </Col>
                      <Col>
                        <Typography
                          className="fw-normal mb-2"
                          color={theme.talentinoBlue}
                          size="15px"
                          bold
                        >
                          {company?.companyName}
                        </Typography>
                        <Typography size="13px" className="lh-sm" regular>
                          {company?.workingLocations &&
                            company.workingLocations.length > 0 && (
                              <div className="mt-2">
                                {t('general.location')}:
                                {company.workingLocations.map(
                                  (location, index) => (
                                    <span className="mx-1" key={location}>
                                      {formatCountryName(location)}
                                      {index <
                                        company.workingLocations.length - 1 &&
                                        ' ,'}
                                    </span>
                                  ),
                                )}
                              </div>
                            )}
                          {company?.website && (
                            <div className="d-flex">
                              {t('general.website')}:{' '}
                              <ExternalLink
                                href={formatWebsiteUrl(company.website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ms-1"
                              >
                                {company.website}
                              </ExternalLink>
                            </div>
                          )}

                          {company?.industry && (
                            <div>
                              {t('card.industry')}: {company.industry}
                            </div>
                          )}
                          {company?.companySize && (
                            <div>
                              {t('card.companySize')}: {company.companySize}
                            </div>
                          )}
                        </Typography>
                      </Col>
                    </Row>
                  </InfoCard>
                </Row>
              </Col>
            </Row>
          </JobApplicationPositionCard>
        </Row>
      </ScrollableContainer>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body>{t('pages.talentPoolLinkCopied')}</Toast.Body>
        </Toast>
      </ToastContainer>

      <StickyFooter>
        <FooterText>
          <span style={{ fontWeight: '600' }}>
            {t('footer.TalentinoAI')}{' '}
            <FooterContent>© {new Date().getFullYear()}</FooterContent>
          </span>
          .{' '}
          <span style={{ fontWeight: '600' }}>
            {t('footer.allRightsReserved')}
          </span>
        </FooterText>
      </StickyFooter>
    </FullPageContainer>
  )
}

TalentPoolInfo.propTypes = {
  talentPool: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    careerPageUrl: PropTypes.string,
    resourceLinks: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }).isRequired,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    logo: PropTypes.string,
    website: PropTypes.string,
    industry: PropTypes.string,
    companySize: PropTypes.string,
    workingLocations: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onApplyClick: PropTypes.func.isRequired,
}

export default TalentPoolInfo
